import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

/** The theme the user chose with the toggle, if any. */
export const readStoredTheme = (): Theme | undefined => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "dark" || stored === "light" ? stored : undefined;
  } catch (e) {
    return undefined;
  }
};

/** What the operating system prefers right now. */
export const systemTheme = (): Theme =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

/** The theme in effect: the stored choice, else the system preference. */
export const currentTheme = (): Theme => readStoredTheme() ?? systemTheme();

/** Stores the choice and applies it; theme.css maps its tokens from the attribute. */
export const applyTheme = (theme: Theme) => {
  document.documentElement.setAttribute("data-theme", theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch (e) {
    // Storage can be unavailable (private mode); the attribute alone is enough for this page load.
  }
};

/**
 * Call once at startup. A stored choice is applied; without one the attribute stays off so theme.css follows the
 * system preference, live. public/index.html does the same before first paint so a stored choice never flashes.
 */
export const applyStoredTheme = () => {
  const stored = readStoredTheme();
  if (stored) {
    document.documentElement.setAttribute("data-theme", stored);
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
};

export const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(currentTheme);

  // Follow the operating system while no choice is stored.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (!readStoredTheme()) {
        setTheme(media.matches ? "dark" : "light");
      }
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next = current === "dark" ? "light" : "dark";
      applyTheme(next);
      return next;
    });
  }, []);

  return { theme, toggleTheme };
};
